import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  CircularProgress,
  InputAdornment,
  Stack,
  useTheme,
  useMediaQuery,
  Button,
  Autocomplete,
  Grid,
  IconButton,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  AccountBalance as AccountBalanceIcon,
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import carteraService, { MovimientoCartera, SaldoCartera } from '../../services/carteraService';
import { formatCurrency, formatDate } from '../../utils/formatters';

// Interfaz para representar una fila con saldo acumulado
interface MovimientoRow {
  movimiento: MovimientoCartera;
  saldoAcumulado?: number;
}

const MovimientosCarteras: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams] = useSearchParams();

  const [movimientos, setMovimientos] = useState<MovimientoCartera[]>([]);
  const [carteras, setCarteras] = useState<SaldoCartera[]>([]);
  const [carteraSeleccionada, setCarteraSeleccionada] = useState<SaldoCartera | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingCarteras, setLoadingCarteras] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const MAX_PAGE_SIZE = 20; // Límite máximo del backend
  const [totalPages, setTotalPages] = useState(0);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [todosLosMovimientos, setTodosLosMovimientos] = useState<MovimientoCartera[]>([]);
  const [fechaDesde, setFechaDesde] = useState<string>('');
  const [fechaHasta, setFechaHasta] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Cargar todos los movimientos para calcular saldo acumulado
  useEffect(() => {
    const cargarTodosLosMovimientos = async () => {
      if (!carteraSeleccionada) {
        setTodosLosMovimientos([]);
        return;
      }

      try {
        const todos: MovimientoCartera[] = [];
        let currentPage = 1;
        const pageSize = MAX_PAGE_SIZE; // Usar el máximo permitido por el backend
        let hasMore = true;

        const fechaDesdeParam = fechaDesde ? `${fechaDesde}T00:00:00` : undefined;
        const fechaHastaParam = fechaHasta ? `${fechaHasta}T23:59:59` : undefined;

        while (hasMore) {
          const response = await carteraService.getMovimientos(carteraSeleccionada.nroBanco, {
            pageNumber: currentPage,
            pageSize,
            fechaDesde: fechaDesdeParam,
            fechaHasta: fechaHastaParam,
            searchTerm: searchTerm.trim() || undefined,
          });

          if (response.items && response.items.length > 0) {
            todos.push(...response.items);
            if (currentPage >= response.totalPages) {
              hasMore = false;
            } else {
              currentPage++;
            }
          } else {
            hasMore = false;
          }
        }

        // Ordenar por fecha
        todos.sort((a, b) => {
          const dateA = new Date(a.fechaEmision).getTime();
          const dateB = new Date(b.fechaEmision).getTime();
          return dateA - dateB;
        });

        setTodosLosMovimientos(todos);
      } catch (error) {
        console.error('Error al cargar todos los movimientos:', error);
        setTodosLosMovimientos([]);
      }
    };

    cargarTodosLosMovimientos();
  }, [carteraSeleccionada, fechaDesde, fechaHasta, searchTerm]);

  // Usar el saldo acumulado que viene directamente de la API (es más confiable que calcularlo en el frontend)
  const filasConSaldo = useMemo<MovimientoRow[]>(() => {
    if (!carteraSeleccionada || movimientos.length === 0) {
      return [];
    }

    // La API ya calcula correctamente el saldoAcumulado, así que lo usamos directamente
    // Si no viene de la API, calculamos un fallback básico
    return movimientos.map(movimiento => {
      // Priorizar el saldoAcumulado de la API
      let saldoAcumulado = movimiento.saldoAcumulado;
      
      // Si la API no proporciona saldoAcumulado, calcular un fallback básico
      // (esto no debería pasar, pero es una medida de seguridad)
      if (saldoAcumulado === undefined || saldoAcumulado === null) {
        // Para el fallback, usar el saldo de la cartera como referencia
        // y calcular desde el primer movimiento de la página
        // Nota: Este cálculo es aproximado y puede no ser exacto con paginación
        const debe = movimiento.debe || 0;
        const haber = movimiento.haber || 0;
        // Si es el primer movimiento de la página, usar el saldo de la cartera como base
        // (esto es solo un fallback aproximado)
        saldoAcumulado = carteraSeleccionada.saldo || 0;
      }
      
      return {
        movimiento,
        saldoAcumulado: saldoAcumulado,
      };
    });
  }, [movimientos, carteraSeleccionada]);

  // Cargar lista de carteras
  useEffect(() => {
    const cargarCarteras = async () => {
      try {
        setLoadingCarteras(true);
        // Cargar todas las carteras usando paginación
        const todasLasCarteras: SaldoCartera[] = [];
        let currentPage = 1;
        let hasMore = true;
        
        while (hasMore) {
          const response = await carteraService.getSaldos({
            pageNumber: currentPage,
            pageSize: MAX_PAGE_SIZE,
          });
          
          if (response.items && response.items.length > 0) {
            todasLasCarteras.push(...response.items);
            if (currentPage >= response.totalPages) {
              hasMore = false;
            } else {
              currentPage++;
            }
          } else {
            hasMore = false;
          }
        }
        
        const response = { items: todasLasCarteras };
        setCarteras(response.items || []);
        
        // Si hay un parámetro nroBanco en la URL, seleccionar esa cartera automáticamente
        const nroBancoParam = searchParams.get('nroBanco');
        if (nroBancoParam) {
          const nroBanco = parseInt(nroBancoParam, 10);
          const carteraEncontrada = response.items?.find(c => c.nroBanco === nroBanco);
          if (carteraEncontrada) {
            setCarteraSeleccionada(carteraEncontrada);
          }
        }
      } catch (error: any) {
        console.error('Error al cargar carteras:', error);
        enqueueSnackbar('Error al cargar las carteras', { variant: 'error' });
      } finally {
        setLoadingCarteras(false);
      }
    };
    cargarCarteras();
  }, [enqueueSnackbar, searchParams]);

  // Cargar movimientos cuando se selecciona una cartera
  const cargarMovimientos = async (nroBanco: number, pageNumber: number = page + 1, pageSize: number = rowsPerPage) => {
    try {
      setLoading(true);
      const fechaDesdeParam = fechaDesde ? `${fechaDesde}T00:00:00` : undefined;
      const fechaHastaParam = fechaHasta ? `${fechaHasta}T23:59:59` : undefined;
      
      // Asegurar que pageSize no exceda el máximo permitido
      const validPageSize = Math.min(pageSize, MAX_PAGE_SIZE);
      
      const response = await carteraService.getMovimientos(nroBanco, {
        pageNumber,
        pageSize: validPageSize,
        fechaDesde: fechaDesdeParam,
        fechaHasta: fechaHastaParam,
        searchTerm: searchTerm.trim() || undefined,
      });
      const movimientosData = response.items || [];
      setMovimientos(movimientosData);
      setTotalCount(response.totalCount || 0);
      setTotalPages(response.totalPages || 0);
    } catch (error: any) {
      console.error('Error al cargar movimientos:', error);
      enqueueSnackbar('Error al cargar los movimientos de la cartera', { variant: 'error' });
      setMovimientos([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  // Debounce para searchTerm
  useEffect(() => {
    if (!carteraSeleccionada) {
      setMovimientos([]);
      setTotalCount(0);
      setTotalPages(0);
      return;
    }

    const timeoutId = setTimeout(() => {
      cargarMovimientos(carteraSeleccionada.nroBanco, 1, rowsPerPage);
      setPage(0);
    }, searchTerm ? 500 : 0);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    if (carteraSeleccionada) {
      cargarMovimientos(carteraSeleccionada.nroBanco, page + 1, rowsPerPage);
    } else {
      setMovimientos([]);
      setTotalCount(0);
      setTotalPages(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carteraSeleccionada, page, rowsPerPage, fechaDesde, fechaHasta]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    // Asegurar que no exceda el máximo permitido
    const validRowsPerPage = Math.min(newRowsPerPage, MAX_PAGE_SIZE);
    setRowsPerPage(validRowsPerPage);
    setPage(0);
  };

  // Calcular totales desde los movimientos
  const totalIngresos = useMemo(() => {
    return todosLosMovimientos.reduce((sum, mov) => sum + (mov.debe || 0), 0);
  }, [todosLosMovimientos]);

  const totalEgresos = useMemo(() => {
    return todosLosMovimientos.reduce((sum, mov) => sum + (mov.haber || 0), 0);
  }, [todosLosMovimientos]);

  const saldoNeto = carteraSeleccionada?.saldo || 0;

  // Obtener todos los movimientos para exportar
  const obtenerTodosLosMovimientos = async (nroBanco: number): Promise<MovimientoCartera[]> => {
    const todosLosMovimientos: MovimientoCartera[] = [];
    let currentPage = 1;
    const pageSize = MAX_PAGE_SIZE; // Usar el máximo permitido por el backend
    let hasMore = true;

        const fechaDesdeParam = fechaDesde ? `${fechaDesde}T00:00:00` : undefined;
        const fechaHastaParam = fechaHasta ? `${fechaHasta}T23:59:59` : undefined;

        while (hasMore) {
          try {
            const response = await carteraService.getMovimientos(nroBanco, {
              pageNumber: currentPage,
              pageSize,
              fechaDesde: fechaDesdeParam,
              fechaHasta: fechaHastaParam,
              searchTerm: searchTerm.trim() || undefined,
            });
        
        if (response.items && response.items.length > 0) {
          todosLosMovimientos.push(...response.items);
          if (currentPage >= response.totalPages) {
            hasMore = false;
          } else {
            currentPage++;
          }
        } else {
          hasMore = false;
        }
      } catch (error) {
        console.error('Error al obtener movimientos:', error);
        hasMore = false;
      }
    }

    return todosLosMovimientos;
  };

  const handleExportExcel = async () => {
    if (!carteraSeleccionada) {
      enqueueSnackbar('Debe seleccionar una cartera', { variant: 'warning' });
      return;
    }

    if (totalCount === 0) {
      enqueueSnackbar('No hay datos para exportar', { variant: 'warning' });
      return;
    }

    try {
      setExportingExcel(true);
      enqueueSnackbar('Obteniendo todos los movimientos para exportar...', { variant: 'info' });

      const todosLosMovimientos = await obtenerTodosLosMovimientos(carteraSeleccionada.nroBanco);

      if (todosLosMovimientos.length === 0) {
        enqueueSnackbar('No hay movimientos para exportar', { variant: 'warning' });
        return;
      }

      // Usar el saldoAcumulado de la API si está disponible, de lo contrario calcular como fallback
      const tieneSaldoAPI = todosLosMovimientos.some(mov => mov.saldoAcumulado !== undefined && mov.saldoAcumulado !== null);
      
      let saldoAcumulado = 0;
      if (!tieneSaldoAPI) {
        // Fallback: calcular saldo inicial procesando movimientos en orden inverso desde el saldo final
        let saldoInicial = carteraSeleccionada.saldo || 0;
        const movimientosInversos = [...todosLosMovimientos].reverse();
        movimientosInversos.forEach(mov => {
          const debe = mov.debe || 0;
          const haber = mov.haber || 0;
          saldoInicial = saldoInicial - debe + haber;
        });
        saldoAcumulado = saldoInicial;
      }

      // Preparar movimientos para exportar
      const datosExportar = todosLosMovimientos.map(movimiento => {
        const debe = movimiento.debe || 0;
        const haber = movimiento.haber || 0;
        
        // Usar saldoAcumulado de la API si está disponible
        let saldo = movimiento.saldoAcumulado;
        if (saldo === undefined || saldo === null) {
          // Fallback: calcular saldo acumulado procesando en orden cronológico
          saldoAcumulado = saldoAcumulado + debe - haber;
          saldo = saldoAcumulado;
        }
        
        return {
          'Nro. Formulario': movimiento.nroFormulario,
          'Fecha': formatDate(movimiento.fechaEmision),
          'Tipo Formulario': movimiento.nombreTipoFormulario?.trim() || movimiento.nroTipoFormulario?.trim() || '',
          'Comprobante': movimiento.letraSucNum?.trim() || `${movimiento.letra?.trim() || ''}${movimiento.nroComprobante || ''}`,
          'Nombre': movimiento.nombre?.trim() || '',
          'MCP': movimiento.mcp?.trim() || '',
          'Tipo Pago': movimiento.tipoPago?.trim() || '',
          'Detalle': movimiento.detalle?.trim() || '',
          'Usuario': movimiento.usuario || '',
          'Debe': debe,
          'Haber': haber,
          'Saldo': saldo,
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(datosExportar);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Movimientos');

      const today = new Date().toISOString().slice(0, 10);
      const fileName = `movimientos_cartera_${carteraSeleccionada.nroBanco}_${today}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      enqueueSnackbar(`Datos exportados exitosamente (${todosLosMovimientos.length} movimientos, ${datosExportar.length} registros)`, { variant: 'success' });
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      enqueueSnackbar('Error al exportar a Excel', { variant: 'error' });
    } finally {
      setExportingExcel(false);
    }
  };

  const handleExportPDF = async () => {
    if (!carteraSeleccionada) {
      enqueueSnackbar('Debe seleccionar una cartera', { variant: 'warning' });
      return;
    }

    if (totalCount === 0) {
      enqueueSnackbar('No hay datos para exportar', { variant: 'warning' });
      return;
    }

    try {
      setExportingPDF(true);
      enqueueSnackbar('Obteniendo todos los movimientos para exportar...', { variant: 'info' });

      const todosLosMovimientos = await obtenerTodosLosMovimientos(carteraSeleccionada.nroBanco);

      if (todosLosMovimientos.length === 0) {
        enqueueSnackbar('No hay movimientos para exportar', { variant: 'warning' });
        return;
      }

      const { jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;

      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const marginX = 40;
      const marginY = 50;
      let currentY = marginY;

      // Título
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Movimientos de Carteras', marginX, currentY);
      currentY += 24;

      // Información de la cartera
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Cartera: ${carteraSeleccionada.nroBanco} - ${carteraSeleccionada.nombreBanco?.trim() || ''}`, marginX, currentY);
      currentY += 16;
      doc.text(`Total Ingresos: ${formatCurrency(totalIngresos)}`, marginX, currentY);
      currentY += 16;
      doc.text(`Total Egresos: ${formatCurrency(totalEgresos)}`, marginX, currentY);
      currentY += 16;
      doc.text(`Saldo Neto: ${formatCurrency(saldoNeto)}`, marginX, currentY);
      currentY += 24;

      // Usar el saldoAcumulado de la API si está disponible, de lo contrario calcular como fallback
      const tieneSaldoAPI = todosLosMovimientos.some(mov => mov.saldoAcumulado !== undefined && mov.saldoAcumulado !== null);
      
      let saldoAcumuladoPDF = 0;
      if (!tieneSaldoAPI) {
        // Fallback: calcular saldo inicial procesando movimientos en orden inverso desde el saldo final
        let saldoInicialPDF = carteraSeleccionada.saldo || 0;
        const movimientosInversosPDF = [...todosLosMovimientos].reverse();
        movimientosInversosPDF.forEach(mov => {
          const debe = mov.debe || 0;
          const haber = mov.haber || 0;
          saldoInicialPDF = saldoInicialPDF - debe + haber;
        });
        saldoAcumuladoPDF = saldoInicialPDF;
      }

      // Preparar movimientos para la tabla PDF
      const tableData = todosLosMovimientos.map(movimiento => {
        const debe = movimiento.debe || 0;
        const haber = movimiento.haber || 0;
        
        // Usar saldoAcumulado de la API si está disponible
        let saldo = movimiento.saldoAcumulado;
        if (saldo === undefined || saldo === null) {
          // Fallback: calcular saldo acumulado procesando en orden cronológico
          saldoAcumuladoPDF = saldoAcumuladoPDF + debe - haber;
          saldo = saldoAcumuladoPDF;
        }
        
        return [
          movimiento.nroFormulario.toString(),
          formatDate(movimiento.fechaEmision),
          movimiento.nombreTipoFormulario?.trim() || movimiento.nroTipoFormulario?.trim() || '',
          movimiento.letraSucNum?.trim() || `${movimiento.letra?.trim() || ''}${movimiento.nroComprobante || ''}`,
          movimiento.nombre?.trim() || '',
          movimiento.mcp?.trim() || '',
          movimiento.tipoPago?.trim() || '',
          movimiento.detalle?.trim() || '',
          debe > 0 ? formatCurrency(debe) : '-',
          haber > 0 ? formatCurrency(haber) : '-',
          formatCurrency(saldo),
          movimiento.usuario || '',
        ];
      });

      autoTable(doc, {
        startY: currentY,
        head: [['Nro. Formulario', 'Fecha', 'Tipo', 'Comprobante', 'Nombre', 'MCP', 'Tipo Pago', 'Detalle', 'Debe', 'Haber', 'Saldo', 'Usuario']],
        body: tableData,
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 6, cellPadding: 2, overflow: 'linebreak' },
        headStyles: { fillColor: [25, 118, 210], textColor: 255, fontSize: 7 },
        columnStyles: {
          0: { cellWidth: 50 },
          1: { cellWidth: 60 },
          2: { cellWidth: 80 },
          3: { cellWidth: 100 },
          4: { cellWidth: 100 },
          5: { cellWidth: 100 },
          6: { cellWidth: 50 },
          7: { cellWidth: 100 },
          8: { halign: 'right', cellWidth: 70 },
          9: { halign: 'right', cellWidth: 70 },
          10: { halign: 'right', cellWidth: 70 },
          11: { cellWidth: 50 },
        },
        tableWidth: 'auto',
      });

      const pdfBlob = doc.output('blob');
      const today = new Date().toISOString().slice(0, 10);
      const fileName = `movimientos_cartera_${carteraSeleccionada.nroBanco}_${today}.pdf`;

      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.target = '_self';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      enqueueSnackbar(`PDF descargado correctamente (${todosLosMovimientos.length} movimientos, ${tableData.length} registros)`, { variant: 'success' });
    } catch (error) {
      console.error('Error al generar el PDF:', error);
      enqueueSnackbar('Error al generar el PDF', { variant: 'error' });
    } finally {
      setExportingPDF(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: '#1976d2',
              }}
            >
              Movimientos de Carteras
            </Typography>
            <Stack direction="row" spacing={1}>
              {carteraSeleccionada && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleExportExcel}
                    disabled={loading || exportingExcel || totalCount === 0}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1,
                      textTransform: 'none',
                    }}
                  >
                    {exportingExcel ? 'Exportando...' : 'Exportar Excel'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PictureAsPdfIcon />}
                    onClick={handleExportPDF}
                    disabled={loading || exportingPDF || totalCount === 0}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1,
                      textTransform: 'none',
                    }}
                  >
                    {exportingPDF ? 'Generando...' : 'Exportar PDF'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => cargarMovimientos(carteraSeleccionada.nroBanco, page + 1, rowsPerPage)}
                    disabled={loading}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1,
                      textTransform: 'none',
                    }}
                  >
                    Actualizar
                  </Button>
                </>
              )}
            </Stack>
          </Stack>

          {/* Selector de cartera */}
          <Box sx={{ mb: 3 }}>
            <Autocomplete
              options={carteras}
              getOptionLabel={(option) => `${option.nroBanco} - ${option.nombreBanco?.trim() || ''}`}
              value={carteraSeleccionada}
              onChange={(event, newValue) => {
                setCarteraSeleccionada(newValue);
              }}
              loading={loadingCarteras}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Seleccionar Cartera"
                  placeholder="Buscar cartera por número o nombre..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <AccountBalanceIcon sx={{ color: '#666' }} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      },
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.nroBanco}>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                    <AccountBalanceIcon sx={{ color: '#1976d2' }} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {option.nombreBanco?.trim() || '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Nro: {option.nroBanco} | Saldo: {formatCurrency(option.saldo || 0)}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              )}
            />
          </Box>

          {/* Resumen de movimientos */}
          {carteraSeleccionada && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <Card sx={{ bgcolor: '#4caf50', color: '#fff', textAlign: 'center', py: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatCurrency(totalIngresos)}
                  </Typography>
                  <Typography variant="caption">Total Ingresos</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ bgcolor: '#f44336', color: '#fff', textAlign: 'center', py: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatCurrency(totalEgresos)}
                  </Typography>
                  <Typography variant="caption">Total Egresos</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ bgcolor: '#1976d2', color: '#fff', textAlign: 'center', py: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatCurrency(saldoNeto)}
                  </Typography>
                  <Typography variant="caption">Saldo Neto</Typography>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Filtros */}
          {carteraSeleccionada && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Buscar"
                  placeholder="Buscar por descripción, nombre, comprobante, letra o usuario..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(0);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#666' }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSearchTerm('');
                            setPage(0);
                          }}
                          sx={{ p: 0.5 }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha Desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => {
                    setFechaDesde(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Stack direction="row" spacing={1}>
                  <TextField
                    fullWidth
                    label="Fecha Hasta"
                    type="date"
                    value={fechaHasta}
                    onChange={(e) => {
                      setFechaHasta(e.target.value);
                      setPage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: '#fafafa',
                        '&:hover': {
                          bgcolor: '#f5f5f5',
                        },
                        '&.Mui-focused': {
                          bgcolor: '#ffffff',
                        },
                      },
                    }}
                  />
                  {(fechaDesde || fechaHasta || searchTerm) && (
                    <Button
                      variant="outlined"
                      startIcon={<ClearIcon />}
                      onClick={() => {
                        setFechaDesde('');
                        setFechaHasta('');
                        setSearchTerm('');
                        setPage(0);
                      }}
                      sx={{
                        borderRadius: 2,
                        px: 2,
                        py: 1,
                        textTransform: 'none',
                        minWidth: 'auto',
                      }}
                    >
                      Limpiar
                    </Button>
                  )}
                </Stack>
              </Grid>
            </Grid>
          )}

          {/* Tabla de movimientos */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : carteraSeleccionada ? (
            filasConSaldo.length > 0 ? (
              <>
                {isMobile ? (
                  <Stack spacing={2}>
                    {filasConSaldo.map((fila, idx) => (
                      <Card
                        key={`${fila.movimiento.nroFormulario}-${idx}`}
                        sx={{
                          borderRadius: 2,
                          border: '1px solid #e0e0e0',
                          '&:hover': {
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                          },
                        }}
                      >
                        <CardContent>
                          <Stack spacing={1.5}>
                            <Box>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Formulario #{fila.movimiento.nroFormulario}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {formatDate(fila.movimiento.fechaEmision)} | Tipo: {fila.movimiento.nombreTipoFormulario?.trim() || fila.movimiento.nroTipoFormulario?.trim() || '-'}
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Comprobante:</strong> {fila.movimiento.letraSucNum?.trim() || `${fila.movimiento.letra?.trim() || ''}${fila.movimiento.nroComprobante || ''}`}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Nombre:</strong> {fila.movimiento.nombre?.trim() || '-'}
                            </Typography>
                            {fila.movimiento.descripcion && (
                              <Typography variant="body2" color="text.secondary">
                                <strong>Descripción:</strong> {fila.movimiento.descripcion.trim()}
                              </Typography>
                            )}
                            <Box sx={{ mt: 1, p: 1.5, bgcolor: '#f9f9f9', borderRadius: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                Medio de Pago:
                              </Typography>
                              <Stack spacing={0.5}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {fila.movimiento.mcp?.trim() || '-'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Tipo: {fila.movimiento.tipoPago?.trim() || '-'}
                                </Typography>
                                {fila.movimiento.detalle?.trim() && (
                                  <Typography variant="caption" color="text.secondary">
                                    {fila.movimiento.detalle.trim()}
                                  </Typography>
                                )}
                              </Stack>
                            </Box>
                            <Box sx={{ mt: 1, p: 1.5, bgcolor: '#f0f0f0', borderRadius: 1 }}>
                              <Stack spacing={0.5}>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Debe:</strong> {fila.movimiento.debe ? formatCurrency(fila.movimiento.debe) : '-'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Haber:</strong> {fila.movimiento.haber ? formatCurrency(fila.movimiento.haber) : '-'}
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontWeight: 600,
                                    color: (fila.saldoAcumulado || 0) < 0 ? '#f44336' : 'inherit'
                                  }}
                                >
                                  <strong>Saldo:</strong> {formatCurrency(fila.saldoAcumulado || 0)}
                                </Typography>
                              </Stack>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              <strong>Usuario:</strong> {fila.movimiento.usuario || '-'}
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                    <TablePagination
                      component="div"
                      count={totalCount}
                      page={page}
                      onPageChange={handleChangePage}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      rowsPerPageOptions={[10, 20]}
                      labelRowsPerPage="Filas por página"
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
                    />
                  </Stack>
                ) : (
                  <TableContainer
                    component={Paper}
                    sx={{
                      borderRadius: 2,
                      boxShadow: 'none',
                      border: '1px solid #e0e0e0',
                    }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Nro. Formulario</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Comprobante</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>MCP</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Tipo Pago</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Detalle</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Debe</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Haber</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Saldo</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Usuario</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filasConSaldo.map((fila, idx) => {
                          const esIngreso = (fila.movimiento.debe || 0) > 0;
                          return (
                            <TableRow
                              key={`${fila.movimiento.nroFormulario}-${idx}`}
                              sx={{
                                bgcolor: esIngreso ? '#e8f5e9' : '#ffebee',
                                '&:hover': {
                                  bgcolor: esIngreso ? '#c8e6c9' : '#ffcdd2',
                                },
                              }}
                            >
                              <TableCell sx={{ fontWeight: 500 }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  {esIngreso ? (
                                    <ArrowUpwardIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                                  ) : (
                                    <ArrowDownwardIcon sx={{ fontSize: 16, color: '#f44336' }} />
                                  )}
                                  <span>{fila.movimiento.nroFormulario}</span>
                                </Stack>
                              </TableCell>
                              <TableCell>{formatDate(fila.movimiento.fechaEmision)}</TableCell>
                              <TableCell>{fila.movimiento.nombreTipoFormulario?.trim() || fila.movimiento.nroTipoFormulario?.trim() || '-'}</TableCell>
                              <TableCell>
                                {fila.movimiento.letraSucNum?.trim() || `${fila.movimiento.letra?.trim() || ''}${fila.movimiento.nroComprobante || ''}`}
                              </TableCell>
                              <TableCell>{fila.movimiento.nombre?.trim() || '-'}</TableCell>
                              <TableCell>{fila.movimiento.mcp?.trim() || '-'}</TableCell>
                              <TableCell>{fila.movimiento.tipoPago?.trim() || '-'}</TableCell>
                              <TableCell>{fila.movimiento.detalle?.trim() || '-'}</TableCell>
                              <TableCell align="right">
                                {fila.movimiento.debe ? formatCurrency(fila.movimiento.debe) : '-'}
                              </TableCell>
                              <TableCell align="right">
                                {fila.movimiento.haber ? formatCurrency(fila.movimiento.haber) : '-'}
                              </TableCell>
                              <TableCell 
                                align="right" 
                                sx={{ 
                                  fontWeight: 600,
                                  color: (fila.saldoAcumulado || 0) < 0 ? '#f44336' : 'inherit'
                                }}
                              >
                                {formatCurrency(fila.saldoAcumulado || 0)}
                              </TableCell>
                              <TableCell>{fila.movimiento.usuario || '-'}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    <TablePagination
                      component="div"
                      count={totalCount}
                      page={page}
                      onPageChange={handleChangePage}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      rowsPerPageOptions={[10, 20]}
                      labelRowsPerPage="Filas por página"
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
                      sx={{
                        borderTop: '1px solid #e0e0e0',
                      }}
                    />
                  </TableContainer>
                )}
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  No se encontraron movimientos para esta cartera
                </Typography>
              </Box>
            )
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <AccountBalanceIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
              <Typography color="text.secondary">
                Selecciona una cartera para ver sus movimientos
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default MovimientosCarteras;
